import { useState } from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import {
  FileStack,
  FileText,
  FolderKanban,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Receipt,
  Repeat,
  ShieldCheck,
} from 'lucide-react';
import { Avatar } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { usePortalLogout, usePortalSession } from './api';

type NavItem = { to: string; label: string; icon: typeof LayoutDashboard; adminOnly?: boolean };

const NAV: NavItem[] = [
  { to: '/portal', label: 'Overview', icon: LayoutDashboard },
  { to: '/portal/projects', label: 'Projects', icon: FolderKanban },
  { to: '/portal/tickets', label: 'Support', icon: LifeBuoy },
  { to: '/portal/services', label: 'Services', icon: Repeat },
  { to: '/portal/documents', label: 'Documents', icon: FileStack },
  { to: '/portal/invoices', label: 'Invoices', icon: Receipt, adminOnly: true },
  { to: '/portal/quotes', label: 'Quotes', icon: FileText, adminOnly: true },
];

/**
 * The client portal shell.
 *
 * Wears the same theme as the internal application but says clearly whose
 * portal it is — a client should never be unsure which company they are
 * looking at, and the finance links simply are not rendered for a standard
 * user, which matches what the server would refuse anyway.
 */
export function PortalShell() {
  const { data: session, isLoading } = usePortalSession();
  const logout = usePortalLogout();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!session) return <Navigate to="/portal/login" replace />;

  const isAdmin = session.portalRole === 'ADMIN';
  const items = NAV.filter((item) => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="sticky top-0 z-40 border-b bg-background">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{session.organization.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              Client portal · {session.company.name}
            </p>
          </div>

          <div className="relative ml-auto">
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted"
            >
              <Avatar name={session.name} size={28} />
              <span className="hidden sm:inline">{session.name}</span>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-1 w-56 rounded-md border bg-background p-1 shadow-md">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{session.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{session.email}</p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <ShieldCheck className="h-3 w-3" />
                    {isAdmin ? 'Account administrator' : 'Standard access'}
                  </p>
                </div>
                <div className="my-1 h-px bg-border" />
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/portal/account');
                  }}
                  className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  Change password
                </button>
                <button
                  onClick={() =>
                    logout.mutate(undefined, { onSuccess: () => navigate('/portal/login') })
                  }
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        <nav className="mx-auto max-w-6xl overflow-x-auto px-4">
          <div className="flex gap-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/portal'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'border-primary font-medium text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6" onClick={() => setMenuOpen(false)}>
        <Outlet />
      </main>

      <footer className="mx-auto max-w-6xl px-4 pb-8 text-xs text-muted-foreground">
        Signed in as {session.email}. Everything on this portal belongs to {session.company.name}.
      </footer>
    </div>
  );
}
